import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { QuoteSettingsService } from '@app/quoting/services/quote-settings.service';
import { ContractLengthClickEvent } from '@app/shared';
import { Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import {distinctUntilChanged, switchMap } from 'rxjs/operators';
import { defaultContractLengths } from '@app/shared/models/default-contract-lengths';
import { SetSweetSpotFilterValue, SetPricesFilters, SetSweetSpotCheckBoxState } from '@utility-information/state/actions/utility-information.actions';
import { productTypesSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector, quoteSelector } from '@app/state/app.selectors';
import { Quote } from '@app/quoting/models/quote';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { QuoteRequestState } from '@app/shared';

@Component({
  selector: 'app-offer-filters',
  templateUrl: './offer-filters.component.html',
  styleUrls: ['./offer-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferFiltersComponent implements OnInit {
  public contactLengthClickEvent: ContractLengthClickEvent;
  @Input() contractLengths;
  frmOfferFilters: FormGroup;
  productTypes: FormControl;
  previousProductTypes: string[] = [''];
  productTypeOptions: string[] = ['Show All', 'Fixed Rate', 'Variable', 'Semi-Variable'];
  selectedContractLengths: number[] = defaultContractLengths;
  maximumSelectedContractLengths = this.selectedContractLengths.length;
  selectQuote$: Subscription;
  route$: any;
  initialProductTypes: string[];
  productTypes$: Observable<string[]>;
  quoteRequestId: string;
  defaultContractLengths: number[];
  productTypesValues: string[];

  public quote: Quote;
  public quoteRequestState: QuoteRequestState;
  quoteRequestType = QuoteRequestType;
  private isReadOnly$: Subscription;
  
  constructor(
    public settingsService: QuoteSettingsService,
    private store: Store<AppState>,) { }

  get productTypesCtrl() {
    return this.frmOfferFilters.get('productTypes');
  }

  get contractLengthsCtrl() {
    return this.frmOfferFilters.get('contractLengths');
  }

  get sweetSpotCtrl() {
    return this.frmOfferFilters.get('sweetSpotCheckbox');
  }

  ngOnInit(): void {
    this.selectQuote$ = this.store.select(quoteSelector).subscribe((s) => {
      this.quote = s;
      this.contractLengths = s.quote;
      this.quoteRequestState = s.quote?.quoteRequestState;
    });
    this.productTypes = new FormControl();
    this.frmOfferFilters = new FormGroup({
      productTypes: this.productTypes,
      sweetSpotCheckbox: new FormControl('checked'),
      contractLengths: new FormControl(),
    });
    this.previousProductTypes = this.productTypeOptions;
    this.store.dispatch(new SetSweetSpotFilterValue(this.quoteRequestId, true));
    this.productTypes$ = this.store.select(productTypesSelector)
    .pipe(
      switchMap(productTypes => {
        distinctUntilChanged();
        if(this.productTypesCtrl?.pristine) {
          this.productTypesValues = productTypes;
          this.productTypesCtrl.setValue(productTypes);
        }

        return of(productTypes)
      })
    );

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      if(isReadOnly) {
        this.frmOfferFilters.disable({ emitEvent: false });
      }
    });
  }

  ngOnDestroy() {
    this.route$?.unsubscribe();
    this.selectQuote$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  public sweetSpotCheckboxChanged(event) {
    this.store.dispatch(new SetPricesFilters(this.quoteRequestId, this.productTypesCtrl.value, this.contractLengthsCtrl.value, this.sweetSpotCtrl.value));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, this.sweetSpotCtrl.value));
  }

  public contractLengthsChanged(event: ContractLengthClickEvent) {
    this.selectedContractLengths = event.selected;
  }

  public productTypeChanged(eventData: any) {

    if(!eventData.value?.includes('Show All') && this.previousProductTypes?.includes('Show All')) {
      this.productTypesCtrl?.setValue(undefined);
    } else if(eventData.value?.includes('Show All') && !this.previousProductTypes?.includes('Show All')) {
      this.productTypesCtrl?.setValue(['Show All', ...this.productTypesValues]);
    } else if(!eventData.value?.includes('Show All') && eventData.value?.length + 1 === eventData.source?.options?.length) {
      this.productTypesCtrl?.setValue(['Show All', ...this.productTypesValues]);
    } else if(eventData.value?.includes('Show All') && eventData.value?.length < eventData.source?.options.length) {
      this.productTypesCtrl?.setValue(eventData.value?.filter(option => option !== 'Show All'));
    }

    this.previousProductTypes = this.productTypesCtrl.value;
    this.store.dispatch(new SetPricesFilters(this.quoteRequestId, this.productTypesCtrl.value, this.contractLengthsCtrl.value, this.sweetSpotCtrl.value));
  }

  allContractLengthsSelected(selected: any) {
    this.frmOfferFilters.get('sweetSpotCheckbox').patchValue(selected);
    this.frmOfferFilters.get('sweetSpotCheckbox').enable();
  }

}
